import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { LoaderService } from "../../../shared/components/loader/loader.service";
import { HeaderComponent } from "../../../shared/layout/app/header/header.component";
import { LeftSideNavComponent } from "../../../shared/layout/app/left-menu/left-side-nav.component";
import {
  ticketColorsModal,
  ticketsImagesBeforeSelecteingc,
} from "../../ticket-booking/ticket-booking.model";
import {
  TicketColorModalType,
  TicketsBllService,
} from "../tickets-bll.service";
import {
  EventMyTickets,
  TicketModel,
  UserEventStatusResp,
} from "../tickets.model";
import { UserService } from "../../../core/services/user.service";
import { ProfileBllService } from "../../profile/profile-bll.service";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { SupportButtonComponent } from "../../../shared/components/support-button/support-button.component";
import { environment } from "../../../../environments/environment";
import { EventDetailsResponseModel } from "../../home/home.model";
import { HomeBllService } from "../../home/home-bll.service";

@Component({
  selector: "app-ticket-list",
  standalone: true,
  imports: [
    LeftSideNavComponent,
    HeaderComponent,
    CommonModule,
    FormsModule,
    LoaderComponent,
    SupportButtonComponent
  ],
  templateUrl: "./ticket-list.component.html",
  styleUrls: ["./ticket-list.component.scss"],
})
export class TicketListComponent implements OnInit {
  /**
   * An array holding the list of tickets fetched or managed by the component.
   * Each ticket is represented by a `TicketModel` object.
   */

  ticketList: TicketModel[] = [];

  /**
   * An object mapping ticket type keys to their corresponding logo image URLs.
   * Each ticket type key maps to an object containing:
   * - `black`: The URL of the black version of the logo.
   * - `color`: The URL of the color version of the logo.
   *
   * Example:
   * {
   *   "standard": { black: "path/to/black-logo.png", color: "path/to/color-logo.png" },
   *   "vip": { black: "path/to/black-vip-logo.png", color: "path/to/color-vip-logo.png" }
   * }
   */
  ticketTypeLogo: {
    [key: string]: { black: string; color: string };
  } = ticketsImagesBeforeSelecteingc;

  /**
   * An object mapping ticket type keys to their corresponding color schemes.
   * Each ticket type contains:
   * - `primary`: The primary color for the ticket type.
   * - `darkPrimary`: A darker variant of the primary color.
   * - `secondary`: The secondary color for the ticket type.
   * - `colorGradientBackground`: The gradient background color for the ticket type.
   *
   * The color values are sourced from `ticketColorsModal`.
   */
  ticketTypeColor: {
    [key: string]: {
      primary: string;
      darkPrimary: string;
      secondary: string;
      colorGradientBackground: string;
    };
  } = ticketColorsModal;

  /**
   * A mapping of ticket type codes to their associated notes.
   * Each note contains two lines of text: `line1` and `line2`.
   * The mapping is initialized from `ticketBLLService.ticketNoteMap` for consistency.
   *
   * Note: This property is only initialized once in the constructor and will not automatically reflect future changes to `ticketNoteMap` unless explicitly updated.
   *       If `ticketNoteMap` is updated after the component is constructed, `ticketNote` will not be updated unless explicitly reassigned.
   *
   * @typeParam string - The ticket identifier.
   * @property line1 - The first line of the note.
   * @property line2 - The second line of the note.
   */
  ticketNote: TicketColorModalType = {};

  /**
   * Holds the current status of the user's event.
   *
   * This property is of type `UserEventStatusResp` or `null` if no status is available.
   * It is typically used to track and display the user's event participation status within the ticket list component.
   */
  userEventStatus: UserEventStatusResp | null = null;

  /**
   * Stores the error message related to the ticket list.
   * This property is set when an error occurs while fetching or processing the ticket list.
   */
  ticketListError: string = "";

  /**
   * Stores the error message related to the user's event status.
   * This string is set when there is an error fetching or updating the user's event status.
   */
  userEventStatusError: string = "";

  /**
   * The number of tickets that have not been assigned to any user.
   * Used to track and display unassigned tickets in the ticket list.
   */
  unassignedTickets: number = 0;

  /**
   * The number of tickets currently assigned to the user.
   * Used to display or track tickets that require the user's attention.
   */
  assignedTickets: number = 0;

  assignedSuccess: boolean = false;

  isZeroAssignedTickets: boolean = true;

  isInitializing: boolean = true;

  supportPhone = environment.support_phone; // Support phone number

  eventDetails: EventDetailsResponseModel | null = null;
  
  /**
   * Creates an instance of TicketListComponent.
   *
   * @param ticketBLLService Service for handling business logic related to tickets.
   * @param router Angular Router service for navigation.
   * @param loaderService Service to control loading indicators.
   */
  constructor(
    private ticketBLLService: TicketsBllService,
    private router: Router,
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private userService: UserService,
    private profileBll: ProfileBllService,
    private homeBll: HomeBllService
  ) {
    // Initialize ticketNote from ticketBLLService.ticketNoteMap for initial state sync.
    // Note: Updates to ticketNoteMap after construction will not automatically update ticketNote.
    this.ticketNote = { ...this.ticketBLLService.ticketNoteMap };
  }

  ngOnInit(): void {
    // Check if 'assigned' query parameter is true
    // If true, set assignedSuccess to true to show the green success message strip
    this.route.queryParams.subscribe((params) => {
      this.assignedSuccess = params["assigned"] === "true";
    });
    //clear summary id on init
    this.ticketBLLService.callClearSummaryId();

    // On component initialization, fetch ticket list and user event status independently.
    // This ensures both ticket data and user status are up-to-date; their order does not affect the outcome.
    this.fetchTicketList();
    this.fetchUserEventStatus();
    this.loadEventDetails();
  }

  /**
   * Indicates whether the loader is currently shown.
   * Returns `true` if the loader service is active, otherwise `false`.
   */
  get isLoading(): boolean {
    return this.loaderService.isShown;
  }

  /**
   * Fetches the list of tickets from the backend service.
   *
   * Displays a loader while the request is in progress. On success, updates the ticket list and clears any error messages.
   * On failure, sets an error message and clears the ticket list. The loader is hidden after the request completes,
   * regardless of the outcome.
   *
   * @remarks
   * This method uses the `ticketBLLService.getTicketList` method to retrieve ticket data for page 1.
   * Errors are logged to the console.
   */
  fetchTicketList(): void {
    this.isInitializing = true;
    this.loaderService.show();
    this.ticketBLLService
      .getTicketList(1)
      .then((result) => {
        this.ticketList = result.data ?? [];
        this.ticketListError = "";
      })
      .catch((err: HttpErrorResponse) => {
        this.ticketListError =
          err.error?.message || err.message || "Unknown error";
        this.ticketList = [];
        if(err.status === 410){
          this.router.navigate(['/home'], { replaceUrl: true });
        }
      })
      .finally(() => {
        this.isInitializing = false;
        this.loaderService.hide();
      });
  }

  /**
   * Fetches the current user's event status from the backend service.
   * On successful retrieval, updates the `userEventStatus` property and clears any previous error messages.
   * Also triggers fetching of event tickets using the user's ticket information.
   * If an error occurs during the fetch, sets an error message and logs the error to the console.
   */
  fetchUserEventStatus(): void {
    this.isInitializing = true;
    this.loaderService.show();
    this.profileBll
      .submitGetUserEventStatus()
      .then((res) => {
        if (res !== null) {
          this.userEventStatusError = "";

          this.userEventStatus = res.data;
          let previewLogo =
            this.userEventStatus.user_profile.image_url ??
            "/assets/images/Default-profile-photo.png";
          let userName =
            this.userEventStatus.user_profile.first_name +
            " " +
            this.userEventStatus.user_profile.last_name;
          this.userService.setUser({
            name: userName, // get from API or JWT
            profileImg: previewLogo, // fallback if empty
            mobile: this.userEventStatus.user_profile.mobile,
            state: this.userEventStatus.user_profile.state,
            city: this.userEventStatus.user_profile.city,
            hasUnassignedTickets: res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
          });

          this.getEventTickets(this.userEventStatus.event_my_tickets);
        }
      })
      .catch((err: any) => {
        this.userEventStatusError =
          err?.error?.message ||
          err?.message ||
          "Error fetching user event status.";
        console.error(
          "Error fetching user event status:",
          err?.error?.message || err?.message || err
        );
      })
      .finally(() => {
        this.isInitializing = false;
        this.loaderService.hide();
      });
  }

  /**
   * Updates the ticket lists for the current event.
   *
   * Assigns the unassigned and assigned tickets from the provided `EventMyTickets` object
   * to the component's respective properties.
   *
   * @param eventMyTickets - An object containing the user's tickets for the event,
   * including unassigned tickets bought by the user and tickets assigned to the user.
   */
  getEventTickets(eventMyTickets: EventMyTickets): void {
    this.unassignedTickets = eventMyTickets.unassigned_tickets_bought_by_me;
    this.assignedTickets = eventMyTickets.tickets_assigned_for_me;
    const bought = eventMyTickets.tickets_bought_by_me;
    const assigned = eventMyTickets.assigned_tickets_bought_by_me;
    // condition: user bought tickets but has not assigned any
    this.isZeroAssignedTickets = bought > 0 && assigned === 0;
    // If user has bought tickets but not assigned any, redirect to assign ticket page
    if (this.isZeroAssignedTickets) {
      this.router.navigate(["/reg/assign-ticket"], { replaceUrl: true });
    }
  }

  /**
   * Formats a mobile number by removing the '+91' country code prefix.
   * If the resulting string is empty, returns 'N/A'.
   *
   * @param mobile - The mobile number string, potentially including the '+91' country code.
   * @returns The mobile number without the country code, or 'N/A' if not available.
   */
  getMobileNo(mobile: string): string {
    // Format the mobile number removing country code and + sign
    const mobileWithoutCountryCode = mobile.replace(/^\+91/, "");
    return mobileWithoutCountryCode === "" ? "N/A" : mobileWithoutCountryCode;
  }

  /**
   * Navigates the user to a different route based on the number of unassigned tickets.
   * - If there are unassigned tickets (`unassignedTickets > 0`), navigates to the assign ticket page.
   * - Otherwise, navigates to the home page.
   *
   * Uses Angular's Router to perform navigation with `replaceUrl` to replace the current history entry.
   */
  goBack(): void {
    if (this.unassignedTickets > 0) {
      this.router.navigate(["/reg/assign-ticket"], { replaceUrl: true });
    } else {
      this.router.navigate(["/home"], { replaceUrl: true });
    }
  }

  /**
   * Navigates the user to the ticket assignment page.
   * This method uses the Angular Router to redirect to the '/reg/assign-ticket' route.
   */
  assignTickets(): void {
    this.router.navigate(["/reg/assign-ticket"]);
  }

  buyTickets() {
    this.router.navigate(["/reg/select-ticket"]);
    // this.router.navigateByUrl("/reg/select-ticket", { replaceUrl: true });
  }

  /**
   * Opens the ticket details view for a given ticket code.
   *
   * Navigates to the ticket details page using Angular Router and passes the ticket code
   * as a query parameter. Can be used to display details for a specific ticket.
   *
   * @param ticketCode - The unique code identifying the ticket to display details for.
   */
  openTicketDetails(ticketCode: string): void {
    console.log("Opening ticket details for ticket code:", ticketCode);

    // Navigate to the ticket details page or open a modal
    // For example, using Angular Router to navigate
    this.router.navigate(["/reg/ticket-details"], {
      queryParams: {
        code: ticketCode,
      },
    });
  }

  goToDigitalCatalogue() {
    this.router.navigate(["catalogue/pavilion-list"]);
  }

  loadEventDetails() {
    this.homeBll.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;     
        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });       
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }
  
}
